import { defineStore } from 'pinia'
import Network from '@/Network'
import type { WebNotification } from '@/types/web-notification.types'

export interface NotificationState {
  notifications: WebNotification[]
  unreadCount: number
  totalCount: number
  isLoading: boolean
  currentPage: number
  pageSize: number
  hasMore: boolean
  pollingIntervalId: ReturnType<typeof setInterval> | null
}

/**
 * Listener de `visibilitychange` por instância da store (função não vai no state).
 * Registrado no `startPolling` e removido no `stopPolling`.
 */
const visibilityListeners = new WeakMap<object, () => void>()

const isTabHidden = (): boolean => typeof document !== 'undefined' && document.hidden

/**
 * Erros: as actions disparadas pelo usuário (abrir a lista, carregar mais, marcar
 * como lida) relançam o `ServiceError` do `WebNotificationService` — quem chama
 * decide o toast via `notifyError` (sem toast na store, rule `error-handling.md`).
 * Só o polling do contador fica silencioso.
 */
export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    notifications: [],
    unreadCount: 0,
    totalCount: 0,
    isLoading: false,
    currentPage: 1,
    pageSize: 20,
    hasMore: true,
    pollingIntervalId: null
  }),

  getters: {
    hasUnread: (state) => state.unreadCount > 0
  },

  actions: {
    async fetchNotifications(page = 1) {
      this.isLoading = true
      try {
        const response = await Network.webNotification.list({
          page,
          limit: this.pageSize
        })
        this.notifications = response.data
        this.totalCount = response.count
        this.unreadCount = response.unreadCount
        this.currentPage = page
        this.hasMore = response.data.length === this.pageSize
      } finally {
        this.isLoading = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.isLoading) return
      this.isLoading = true
      try {
        const nextPage = this.currentPage + 1
        const response = await Network.webNotification.list({
          page: nextPage,
          limit: this.pageSize
        })
        this.notifications.push(...response.data)
        this.totalCount = response.count
        this.unreadCount = response.unreadCount
        this.currentPage = nextPage
        this.hasMore = response.data.length === this.pageSize
      } finally {
        this.isLoading = false
      }
    },

    async fetchUnreadCount() {
      try {
        const response = await Network.webNotification.getSummary()
        this.unreadCount = response.unreadCount
      } catch {
        // fallback intencional: polling em background não pode quebrar a UI — o
        // contador mantém o último valor e a próxima consulta tenta de novo.
      }
    },

    async markAsRead(id: number) {
      await Network.webNotification.markAsRead(id)
      const notification = this.notifications.find((n) => n.id === id)
      if (notification && !notification.isRead) {
        notification.isRead = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }
    },

    async markAllAsRead() {
      await Network.webNotification.markAllAsRead()
      this.notifications.forEach((n) => {
        n.isRead = true
      })
      this.unreadCount = 0
    },

    /**
     * Polling do contador de não lidas (GET /web-notifications/summary, ~42% do
     * tráfego da API). Só consulta com a aba visível: oculta ⇒ para o intervalo;
     * voltou ⇒ 1 consulta imediata e o intervalo recomeça. Intervalo inalterado.
     */
    startPolling(intervalMs = 60000) {
      this.stopPolling()
      const resume = () => {
        if (this.pollingIntervalId) return
        this.fetchUnreadCount()
        this.pollingIntervalId = setInterval(() => {
          this.fetchUnreadCount()
        }, intervalMs)
      }
      const onVisibilityChange = () => {
        if (isTabHidden()) this.clearPollingInterval()
        else resume()
      }
      visibilityListeners.set(this, onVisibilityChange)
      document.addEventListener('visibilitychange', onVisibilityChange)
      if (!isTabHidden()) resume()
    },

    stopPolling() {
      this.clearPollingInterval()
      const listener = visibilityListeners.get(this)
      if (listener) {
        document.removeEventListener('visibilitychange', listener)
        visibilityListeners.delete(this)
      }
    },

    clearPollingInterval() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId)
        this.pollingIntervalId = null
      }
    }
  }
})
